import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material'
import { Mutator } from 'final-form'
import combinedQuery from 'graphql-combine-query'
import { get, merge, sortBy } from 'lodash'
import { Fragment, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import {
  Feature_Base,
  Feature_Base_Bool_Exp,
  Feature_Config,
  Feature_Config_Bool_Exp,
  Feature_Sorting_Config_Bool_Exp,
  Feature_Type_Config,
  Feature_Value,
  Kap_Measure,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  bulkSaveFeatures,
  queryFeatureBase,
  queryFeaturesByFeatureType,
} from 'src/screens/shared/feature/featureSortingConfigQueries'
import {
  FeatureBaseEditUtils,
  FeatureItem,
  FormValues,
  RenderFeatureGroups,
  TextFeatureData,
  TextFieldValue,
} from 'src/screens/shared/feature/utils/FeatureBaseEditUtils'
import { isFeatureSelectionInvalid } from 'src/screens/shared/feature/utils/FeatureValidationUtils'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { TEXT_LENGTH } from 'src/shared/constants/constants'
import { TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'
import { TextField } from 'src/shared/form/control/TextField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { composeValidators, maxChar, required } from 'src/shared/form/validation/validators'
import { InfoIcon } from 'src/shared/icons/Icons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'
import { CombinedError, useClient } from 'urql'
import { FeatureBaseCheckboxData, FeatureBaseCheckboxGroupField } from './FeatureBaseCheckboxGroupField'

const CardContentStyled = styled(CardContent)`
  padding: 0;
  padding-top: ${({ theme }) => theme.spacing(1)};

  &:last-child {
    padding-bottom: ${({ theme }) => theme.spacing(0.5)};
  }
`

const InfoIconStyled = styled(InfoIcon)`
  margin-left: ${({ theme }) => theme.spacing(1)};
`
const TitleStyledGroup = styled(Typography)`
  ${TYPOGRAPHY_MIXIN.subtitle2};
  font-style: normal;
`

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface FeatureBaseEditProps {
  onBack: (featureGroupConfigCode: string) => void
  onSaveCallback?: () => void
  featureBaseWhere: Feature_Base_Bool_Exp
  featureConfigWhere: Feature_Config_Bool_Exp
  featureSortingConfigWhere: Feature_Sorting_Config_Bool_Exp
  featureTypeId: number
  requiredSelection?: boolean
  relatedEntity?: Kap_Measure
}

const decorators = createDecorators()

export const FeatureBaseEdit = ({
  onBack,
  featureBaseWhere,
  featureConfigWhere,
  featureSortingConfigWhere,
  featureTypeId,
  requiredSelection,
  onSaveCallback,
  relatedEntity,
}: FeatureBaseEditProps): ReactElement => {
  const notificationService = useNotificationService()
  const locale = useUserLocale()
  const urqlClient = useClient()
  const { getMessage } = useMessageSource()

  const originRef = useRef<originType>('SAVE')
  const [serverSavedValues, setServerSavedValues] = useState<FormValues | undefined>(undefined)
  const [initialFormValues, setInitialFormValues] = useState<FormValues | undefined>(undefined)
  const [serverData, setServerData] = useState<{
    featureBase: Feature_Base
    featureConfigs: Feature_Config[]
    featureTypeConfig: Feature_Type_Config
  }>()
  const [serverError, setServerError] = useState<CombinedError>()
  const [allFeatures, setAllFeatures] = useState<RenderFeatureGroups[]>([])

  const featureValidation = relatedEntity && relatedEntity?.factsheet !== null

  const relatedEntityFactsheetFeatures = useMemo(() => {
    return (
      relatedEntity?.factsheet?.feature_base?.feature_values.map((featureValue) => featureValue.feature_config.id) ?? []
    )
  }, [relatedEntity])

  // fetch data
  useEffect(() => {
    const initData = async () => {
      const { document, variables } = combinedQuery('FeatureBasesAndFeatureConfigs')
        .add(queryFeatureBase, {
          featureBaseWhere: featureBaseWhere,
          featureTypeId: featureTypeId,
        })
        .add(queryFeaturesByFeatureType, {
          featureType: featureTypeId,
          featureConfigWhere: featureConfigWhere,
          featureSortingConfigWhere: featureSortingConfigWhere,
          featureGroupSortingConfigWhere: featureSortingConfigWhere,
        })

      const { data, error } = await urqlClient
        .query<
          {
            feature_base: Query_Root['feature_base']
            feature_config: Query_Root['feature_config']
            feature_type_config: Query_Root['feature_type_config']
          },
          {
            featureBaseWhere: Feature_Base_Bool_Exp
            featureTypeId: number
            featureType: number
            featureConfigWhere: Feature_Config_Bool_Exp
          }
        >(document, variables!)
        .toPromise()

      if (data) {
        const featureConfigs = sortBy(data.feature_config, [
          'feature_group_config.feature_group_sorting_configs[0].sort_order',
          'feature_sorting_configs[0].sort_order',
        ])

        setServerData({
          featureConfigs: featureConfigs,
          featureTypeConfig: data.feature_type_config[0],
          featureBase: data.feature_base[0],
        })

        const serverFormValues: FormValues = FeatureBaseEditUtils.adaptInitialValues(
          data?.feature_base?.[0]?.feature_values ?? [],
          data?.feature_config ?? [],
          false,
        )

        const initialFormValues: FormValues = FeatureBaseEditUtils.adaptInitialValues(
          data?.feature_base?.[0]?.feature_values ?? [],
          data?.feature_config ?? [],
          true,
        )

        if (featureValidation) {
          const featureTypeHasFactsheetValidation = data.feature_type_config[0].has_kap_factsheet_validation
          const validSelectedFeatures = Object.values(initialFormValues)
            .flatMap((featureGroup) => featureGroup.features?.checkboxes ?? [])
            .filter(
              (featureConfigId) =>
                relatedEntityFactsheetFeatures.includes(featureConfigId) || !featureTypeHasFactsheetValidation,
            )

          const validSelectedOtherFeature = Object.values(initialFormValues)
            .flatMap((featureGroup) => featureGroup.features?.textFields ?? [])
            .filter(
              (feature) =>
                feature !== undefined &&
                (relatedEntityFactsheetFeatures.includes(feature.id) || !featureTypeHasFactsheetValidation),
            )
            .map((feature) => feature?.id)

          const validFeatureConfigs = data?.feature_config.filter(
            (feature) => validSelectedFeatures.includes(feature.id) || validSelectedOtherFeature.includes(feature.id),
          )

          const validServerFormValues: FormValues = FeatureBaseEditUtils.adaptInitialValues(
            data?.feature_base?.[0]?.feature_values ?? [],
            validFeatureConfigs,
            false,
          )

          const validInitialFormValues: FormValues = FeatureBaseEditUtils.adaptInitialValues(
            data?.feature_base?.[0]?.feature_values ?? [],
            validFeatureConfigs,
            true,
          )

          setServerSavedValues(validServerFormValues)
          setInitialFormValues(validInitialFormValues)
        } else {
          setServerSavedValues(serverFormValues)
          setInitialFormValues(initialFormValues)
        }
      } else {
        setServerError(error)
      }
    }

    initData()
  }, [
    urqlClient,
    featureTypeId,
    featureBaseWhere,
    featureConfigWhere,
    featureValidation,
    relatedEntity,
    relatedEntityFactsheetFeatures,
  ])

  useEffect(() => {
    if (serverError) {
      notificationService.operationFailed(serverError)
    }
    if (serverData?.featureConfigs) {
      setAllFeatures(
        FeatureBaseEditUtils.adaptForRendering(
          serverData.featureConfigs,
          serverData.featureBase?.feature_values ?? [],
          locale,
          featureValidation,
          serverData?.featureTypeConfig,
          relatedEntity,
        ),
      )
    }
  }, [
    locale,
    notificationService,
    serverData?.featureConfigs,
    serverData?.featureBase,
    serverError,
    relatedEntity,
    featureTypeId,
    featureValidation,
    serverData?.featureTypeConfig,
  ])

  // handlers
  const updateOtherFeature: Mutator<FormValues, Partial<FormValues>> = (args, state, { changeValue }) => {
    const [groupCode, checked, textFieldValue] = args
    const featureId = textFieldValue.id

    changeValue(state, `${groupCode}.features.textFields[${featureId}].id`, () => featureId)
    changeValue(
      state,
      `${groupCode}.features.textFields[${featureId}].featureConfig`,
      () => textFieldValue.featureConfig,
    )

    if (
      !checked ||
      (relatedEntity &&
        serverData &&
        !isFeatureSelectionInvalid(serverData?.featureTypeConfig, relatedEntity, textFieldValue.featureConfig))
    ) {
      changeValue(state, `${groupCode}.features.textFields[${featureId}].text`, () => '')
    } else {
      // you can't focus a field that's been disabled -> we schedule the focusing to be done in the next microtask
      setTimeout(() => document.getElementById(`${groupCode}.features.textFields[${featureId}].text`)?.focus(), 0)
    }
  }

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }

    document
      .getElementById('features-base-form')
      ?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const handleSubmitLocal = async (values: FormValues): Promise<any> => {
    const selectedItems = Object.keys(values).flatMap((key) => values[key].features.checkboxes)
    const otherFeatures = Object.keys(values)
      .flatMap((key) => values[key]?.features?.textFields ?? [])
      .filter((value) => value != undefined && value.selected)

    if (selectedItems.length === 0 && otherFeatures.length === 0 && requiredSelection) {
      notificationService.error(getMessage('notification.required.features'))
      return
    }

    const serverSavedOtherFeatures =
      serverSavedValues &&
      Object.values(serverSavedValues)
        .map((fg) => fg.features?.textFields ?? [])
        .flat()
        .filter(Boolean)

    const invalidOtherFeatures =
      serverSavedOtherFeatures?.filter(
        (savedFeature) => savedFeature && relatedEntityFactsheetFeatures.indexOf(savedFeature.id) < 0,
      ) ?? []

    const featuresToBeAdded = FeatureBaseEditUtils.createFeatureValues(
      values,
      initialFormValues || ({} as FormValues),
      serverData!.featureBase!.id,
      false,
    )

    const featuresToBeDeleted = FeatureBaseEditUtils.createFeatureValues(
      values,
      serverSavedValues || ({} as FormValues),
      serverData!.featureBase!.id,
      true,
      invalidOtherFeatures.length > 0,
    ).map((x: Feature_Value) => x.feature_config_id)

    const { error } = await urqlClient
      .mutation<
        {
          insert_feature_value: Mutation_Root['insert_feature_value']
          delete_feature_value: Mutation_Root['delete_feature_value']
        },
        {
          featureBaseId: number | undefined
          featuresToBeAdded: Feature_Value[]
          featuresToBeDeleted: number[]
        }
      >(bulkSaveFeatures, {
        featureBaseId: serverData?.featureBase.id,
        featuresToBeAdded,
        featuresToBeDeleted,
      })
      .toPromise()

    if (error) {
      notificationService.operationFailed()
    } else {
      setInitialFormValues(values)
      const newServerData = {} as typeof serverData
      merge(newServerData, serverData)
      setServerData(newServerData)
      notificationService.changesSaved()
      if (onSaveCallback) {
        onSaveCallback()
      }
      const featureGroupConfigCode = serverData?.featureTypeConfig?.code ?? ''
      if (originRef.current === 'SAVE_AND_BACK') {
        onBack(featureGroupConfigCode)
      }
    }
  }

  // JSX
  const renderTextInputFeatureValue = (
    x: RenderFeatureGroups,
    featureItem: TextFeatureData,
    values: FormValues,
    resetFieldState: (name: string | number) => void,
    updateOtherFeature: (...args: any[]) => any,
  ) => {
    const textFieldValue: TextFieldValue = values[x.code]?.features?.textFields?.find(
      (tf) => tf?.id === featureItem.id,
    ) || {
      id: featureItem.id,
      featureConfig: featureItem.featureConfig,
      selected: false,
      text: featureItem.text ?? '',
    }

    return (
      <FeatureBaseCheckboxGroupField
        name={`${x.code}.features.textFields[${featureItem.id}].selected`}
        key={`${x.code}.features.textFields[${textFieldValue.id}]`}
        data={{
          value: featureItem.selected,
        }}
        onClick={(e) => {
          // we intercept the click call so we can clear the corresponding Other TextField
          const target = e.target as HTMLFormElement
          const checked = target.checked
          updateOtherFeature(x.code, checked, textFieldValue)
          resetFieldState(`${x.code}.features.textFields[${featureItem.id}].text`)
        }}
        disabled={disabledOtherFeature(textFieldValue.featureConfig)}
        $withTextField={true}
      >
        {/* inspired by: https://final-form.org/docs/react-final-form/types/FieldProps#validate */}
        <Field
          name={`${x.code}.features.textFields[${featureItem.id}].text`}
          validate={(value, allValues: unknown) => {
            // @ts-ignore
            const currentFieldState = allValues[x.code]?.features?.textFields?.[featureItem.id]?.selected ?? false

            // Skip validation if  not selected
            if (!currentFieldState) {
              return undefined
            }

            return composeValidators(required(), maxChar(TEXT_LENGTH.S))(value)
          }}
        >
          {({ meta }) => (
            <TextField
              key={`${x.code}.features.textFields[${featureItem.id}]`}
              name={`${x.code}.features.textFields[${featureItem.id}].text`}
              label={featureItem.label}
              id={`${x.code}.features.textFields[${featureItem.id}].text`}
              disabled={!values[x.code]?.features?.textFields?.[featureItem.id]?.selected ?? false}
              required={values[x.code]?.features?.textFields?.[featureItem.id]?.selected ?? false}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
        {featureItem.tooltip && (
          <Tooltip title={featureItem.tooltip ?? ''}>
            <InfoIconStyled />
          </Tooltip>
        )}
      </FeatureBaseCheckboxGroupField>
    )
  }

  const renderCheckboxFeatureValue = (x: RenderFeatureGroups, featureItem: FeatureBaseCheckboxData) => {
    return (
      <FeatureBaseCheckboxGroupField
        key={`checkbox-${featureItem.value}`}
        name={`${x.code}.features.checkboxes`}
        data={[featureItem]}
      />
    )
  }

  const renderFeatureValue = (
    x: RenderFeatureGroups,
    featureItem: FeatureItem,
    values: FormValues,
    resetFieldState: (name: string | number) => void,
    updateOtherFeature: (...args: any[]) => any,
  ) => {
    if ('value' in featureItem) {
      return renderCheckboxFeatureValue(x, featureItem)
    } else if ('id' in featureItem) {
      return renderTextInputFeatureValue(x, featureItem, values, resetFieldState, updateOtherFeature)
    }
  }

  const disabledOtherFeature = (featureConfig: Feature_Config) => {
    return featureValidation && isFeatureSelectionInvalid(serverData?.featureTypeConfig, relatedEntity, featureConfig)
  }

  return (
    <>
      <ScreenLayout
        title={getMessage('label.edit.feature.type', [get(serverData?.featureTypeConfig?.names, locale, '')])}
        hasSecondLevelNavigation={false}
        onBack={() => onBack(serverData?.featureTypeConfig?.code ?? '')}
        actions={
          <>
            <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
            <SaveButton origin="header" onClick={onSubmit('SAVE')} />
          </>
        }
      >
        <PageLayout>
          <HelpAndInstructions
            labelKey={get(serverData?.featureTypeConfig?.description, locale, '')}
            defaultExpansion
          />
          <Card>
            <CardContentStyled>
              <>
                {initialFormValues && (
                  <Form
                    initialValues={initialFormValues}
                    onSubmit={handleSubmitLocal}
                    decorators={decorators}
                    mutators={{
                      updateOtherFeature,
                    }}
                    render={({
                      handleSubmit,
                      values,
                      form: {
                        mutators: { updateOtherFeature },
                        resetFieldState,
                      },
                    }) => {
                      return (
                        <form onSubmit={handleSubmit} id="features-base-form">
                          {allFeatures.map((x: RenderFeatureGroups, i: number) => {
                            if (x.features.length) {
                              if (x.name !== '') {
                                return (
                                  <Fragment key={i}>
                                    <Box mx={2} my={1}>
                                      <TitleStyledGroup mb={1}>{x.name}</TitleStyledGroup>
                                      {x.features.map((featureItem, index) => (
                                        <Box key={index} my={0.5}>
                                          {renderFeatureValue(
                                            x,
                                            featureItem,
                                            values,
                                            resetFieldState,
                                            updateOtherFeature,
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Fragment>
                                )
                              } else {
                                return (
                                  <Fragment key={i}>
                                    <Box mb={1}>
                                      {x.features.map((featureItem, item) => (
                                        <Box key={item} my={0.5}>
                                          {renderFeatureValue(
                                            x,
                                            featureItem,
                                            values,
                                            resetFieldState,
                                            updateOtherFeature,
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Fragment>
                                )
                              }
                            }
                          })}
                          <DirtyFormSpy />
                        </form>
                      )
                    }}
                  />
                )}
              </>
            </CardContentStyled>
          </Card>
        </PageLayout>
      </ScreenLayout>
    </>
  )
}
